
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CenterFocusStrong, ZoomIn, ZoomOut } from '@mui/icons-material'
import '../../scss/components/_drawing-workspace.scss'

/**
 * Просмотр чертежа DXF, как в CAD: колесо мыши — масштаб к курсору,
 * перетаскивание — сдвиг, двойной клик — приблизить, щипок двумя пальцами —
 * масштаб на тачпаде и телефоне. Линии остаются тонкими и чёткими при любом
 * увеличении: меняется видимая часть чертежа, а не размер картинки.
 *
 * Картинку SVG рисует сервер (GET /messages/:id/attachment/drawing), в ней
 * только линии и подписи. На всякий случай перед показом из неё убирается
 * всё исполняемое.
 */

const MAX_ZOOM = 400 // во сколько раз можно приблизить
const MIN_ZOOM = 0.5
const WHEEL_STEP = 1.2

/** SVG из файла без скриптов и обработчиков событий. */
const sanitizeSvg = (text) => {
    if (typeof DOMParser === 'undefined') return null
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml')
    const svg = doc.documentElement
    if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length) return null

    svg.querySelectorAll('script, foreignObject, iframe, object, embed').forEach((node) => node.remove())
    svg.querySelectorAll('*').forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
            const name = attribute.name.toLowerCase()
            const value = String(attribute.value || '').trim().toLowerCase()
            if (name.startsWith('on')) node.removeAttribute(attribute.name)
            if ((name === 'href' || name === 'xlink:href') && !value.startsWith('#')) node.removeAttribute(attribute.name)
        })
    })

    return svg
}

const parseViewBox = (svg) => {
    const parts = String(svg?.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number)
    if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
        return { x: parts[0], y: parts[1], w: parts[2], h: parts[3] }
    }
    return null
}

/** Весь чертёж в окне с такими пропорциями: viewBox по форме окна. */
const fitView = (base, width, height) => {
    if (!base || !(width > 0) || !(height > 0)) return base
    const aspect = width / height
    if (base.w / base.h > aspect) {
        const h = base.w / aspect
        return { x: base.x, y: base.y - (h - base.h) / 2, w: base.w, h }
    }
    const w = base.h * aspect
    return { x: base.x - (w - base.w) / 2, y: base.y, w, h: base.h }
}

export default function DrawingWorkspace({ blob, title = 'Чертёж' }) {
    const containerRef = useRef(null)
    const hostRef = useRef(null)
    const svgRef = useRef(null)
    const baseRef = useRef(null)
    const fitRef = useRef(null)
    const viewRef = useRef(null)
    const pointersRef = useRef(new Map())
    const gestureRef = useRef(null)

    const [status, setStatus] = useState('loading') // loading | ready | error
    const [zoomPercent, setZoomPercent] = useState(100)
    const [dragging, setDragging] = useState(false)

    const applyView = useCallback((view) => {
        if (!view || !svgRef.current) return
        viewRef.current = view
        svgRef.current.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`)
        const fit = fitRef.current
        if (fit) setZoomPercent(Math.round((fit.w / view.w) * 100))
    }, [])

    const fitToWindow = useCallback(() => {
        const container = containerRef.current
        if (!container || !baseRef.current) return
        const rect = container.getBoundingClientRect()
        fitRef.current = fitView(baseRef.current, rect.width, rect.height)
        applyView(fitRef.current)
    }, [applyView])

    // Загрузка SVG.
    useEffect(() => {
        let cancelled = false
        setStatus('loading')

        const run = async () => {
            try {
                const text = typeof blob?.text === 'function' ? await blob.text() : String(blob || '')
                if (cancelled) return

                const svg = sanitizeSvg(text)
                const base = parseViewBox(svg)
                const host = hostRef.current
                if (!svg || !base || !host) throw new Error('bad svg')

                svg.setAttribute('width', '100%')
                svg.setAttribute('height', '100%')
                svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
                svg.setAttribute('class', 'drawing-workspace__svg')
                host.replaceChildren(document.importNode(svg, true))
                svgRef.current = host.querySelector('svg')
                baseRef.current = base
                setStatus('ready')
            } catch (_error) {
                if (!cancelled) setStatus('error')
            }
        }

        run()
        return () => {
            cancelled = true
        }
    }, [blob])

    // Чертёж готов или окно поменяло размер — показываем целиком.
    useLayoutEffect(() => {
        if (status !== 'ready') return undefined
        fitToWindow()

        const container = containerRef.current
        if (!container || typeof ResizeObserver === 'undefined') return undefined

        const observer = new ResizeObserver(() => {
            const rect = container.getBoundingClientRect()
            const previousFit = fitRef.current
            const current = viewRef.current
            fitRef.current = fitView(baseRef.current, rect.width, rect.height)
            if (!previousFit || !current) {
                applyView(fitRef.current)
                return
            }
            // Сохраняем масштаб и центр, меняем только пропорции окна.
            const scale = current.w / previousFit.w
            const cx = current.x + current.w / 2
            const cy = current.y + current.h / 2
            const w = fitRef.current.w * scale
            const h = fitRef.current.h * scale
            applyView({ x: cx - w / 2, y: cy - h / 2, w, h })
        })
        observer.observe(container)
        return () => observer.disconnect()
    }, [applyView, fitToWindow, status])

    /** Масштаб в factor раз вокруг точки окна (clientX, clientY). */
    const zoomAt = useCallback((factor, clientX, clientY) => {
        const view = viewRef.current
        const fit = fitRef.current
        const container = containerRef.current
        if (!view || !fit || !container) return

        const rect = container.getBoundingClientRect()
        const minW = fit.w / MAX_ZOOM
        const maxW = fit.w / MIN_ZOOM
        const w = Math.min(maxW, Math.max(minW, view.w / factor))
        const realFactor = view.w / w
        if (Math.abs(realFactor - 1) < 1e-6) return

        const px = clientX === undefined ? 0.5 : (clientX - rect.left) / rect.width
        const py = clientY === undefined ? 0.5 : (clientY - rect.top) / rect.height
        const pointX = view.x + px * view.w
        const pointY = view.y + py * view.h
        const h = view.h / realFactor

        applyView({ x: pointX - px * w, y: pointY - py * h, w, h })
    }, [applyView])

    // Колесо: обработчик не «пассивный», чтобы страница не прокручивалась.
    useEffect(() => {
        const container = containerRef.current
        if (!container || status !== 'ready') return undefined

        const onWheel = (event) => {
            event.preventDefault()
            // Тачпад присылает мелкие шаги — масштаб плавный.
            const strength = Math.min(1, Math.abs(event.deltaY) / 100)
            const factor = Math.pow(WHEEL_STEP, strength || 1)
            zoomAt(event.deltaY < 0 ? factor : 1 / factor, event.clientX, event.clientY)
        }

        container.addEventListener('wheel', onWheel, { passive: false })
        return () => container.removeEventListener('wheel', onWheel)
    }, [status, zoomAt])

    const handlePointerDown = (event) => {
        if (event.button !== undefined && event.button !== 0) return
        if (event.target?.closest?.('button')) return
        event.currentTarget.setPointerCapture?.(event.pointerId)
        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

        const points = [...pointersRef.current.values()]
        if (points.length === 2) {
            const [a, b] = points
            gestureRef.current = { type: 'pinch', distance: Math.hypot(a.x - b.x, a.y - b.y) }
        } else {
            gestureRef.current = { type: 'pan', x: event.clientX, y: event.clientY }
            setDragging(true)
        }
    }

    const handlePointerMove = (event) => {
        if (!pointersRef.current.has(event.pointerId)) return
        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

        const gesture = gestureRef.current
        const view = viewRef.current
        const container = containerRef.current
        if (!gesture || !view || !container) return

        if (gesture.type === 'pinch') {
            const [a, b] = [...pointersRef.current.values()]
            if (!a || !b) return
            const distance = Math.hypot(a.x - b.x, a.y - b.y)
            if (gesture.distance > 0 && distance > 0) {
                zoomAt(distance / gesture.distance, (a.x + b.x) / 2, (a.y + b.y) / 2)
            }
            gesture.distance = distance
            return
        }

        const rect = container.getBoundingClientRect()
        const dx = ((event.clientX - gesture.x) / rect.width) * view.w
        const dy = ((event.clientY - gesture.y) / rect.height) * view.h
        gesture.x = event.clientX
        gesture.y = event.clientY
        applyView({ ...view, x: view.x - dx, y: view.y - dy })
    }

    const handlePointerUp = (event) => {
        pointersRef.current.delete(event.pointerId)
        try {
            event.currentTarget.releasePointerCapture?.(event.pointerId)
        } catch (_error) {
            // Захват уже снят браузером.
        }

        const left = [...pointersRef.current.entries()]
        if (left.length === 1) {
            const [, point] = left[0]
            gestureRef.current = { type: 'pan', x: point.x, y: point.y }
            return
        }
        if (!left.length) {
            gestureRef.current = null
            setDragging(false)
        }
    }

    // Клавиши: + и − — масштаб, 0 — весь чертёж.
    useEffect(() => {
        if (status !== 'ready') return undefined
        const onKey = (event) => {
            if (event.target?.closest?.('input, textarea, [contenteditable="true"]')) return
            if (event.key === '+' || event.key === '=') zoomAt(1.25)
            else if (event.key === '-' || event.key === '_') zoomAt(1 / 1.25)
            else if (event.key === '0') fitToWindow()
            else return
            event.preventDefault()
            event.stopPropagation()
        }
        window.addEventListener('keydown', onKey, true)
        return () => window.removeEventListener('keydown', onKey, true)
    }, [fitToWindow, status, zoomAt])

    return (
        <div className="drawing-workspace">
            <div
                ref={containerRef}
                className={`drawing-workspace__canvas ${dragging ? 'is-dragging' : ''}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onDoubleClick={(event) => zoomAt(2, event.clientX, event.clientY)}
                role="img"
                aria-label={title}
            >
                <div ref={hostRef} className="drawing-workspace__host" />

                {status === 'loading' ? (
                    <div className="drawing-workspace__state">
                        <span className="scene-screenshots-gallery__spinner" />
                        Рисуем чертёж…
                    </div>
                ) : null}
                {status === 'error' ? (
                    <div className="drawing-workspace__state is-error">
                        Не удалось показать чертёж. Скачайте оригинал кнопкой сверху.
                    </div>
                ) : null}
            </div>

            {status === 'ready' ? (
                <div className="drawing-workspace__toolbar" aria-label="Масштаб чертежа">
                    <button type="button" onClick={() => zoomAt(1 / 1.5)} title="Отдалить (−)" aria-label="Отдалить">
                        <ZoomOut fontSize="small" />
                    </button>
                    <span className="drawing-workspace__zoom">{zoomPercent}%</span>
                    <button type="button" onClick={() => zoomAt(1.5)} title="Приблизить (+)" aria-label="Приблизить">
                        <ZoomIn fontSize="small" />
                    </button>
                    <button type="button" onClick={fitToWindow} title="Весь чертёж (0)" aria-label="Весь чертёж">
                        <CenterFocusStrong fontSize="small" />
                    </button>
                    <span className="drawing-workspace__hint">Колесо — масштаб · перетащите — сдвиг · двойной клик — ближе</span>
                </div>
            ) : null}
        </div>
    )
}
